import React, { useState, useEffect, useMemo } from "react";
import ReportLayout, { DateRangePicker } from "./components/ReportLayout";
import { Clock, Building2, ChevronDown } from "lucide-react";
import useHourlySales from "@/hooks/useHourlySales";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
const formatDate = (d) =>
  d instanceof Date ? d.toISOString().split("T")[0] : d;

export default function HourlySalesReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.SALES.SCREEN)) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">
          You do not have permission to access Hourly Sales Report
        </p>
      </div>
    );
  }
  const today = formatDate(new Date());

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [isCustomDate, setIsCustomDate] = useState(false);

  const [sortColumn, setSortColumn] = useState("net_sales");
  const [sortDirection, setSortDirection] = useState("DESC");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [searchQuery, setSearchQuery] = useState("");

  const { data, summary, branchSummary, totalItems, loading, setFilters } = useHourlySales();

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const applyPreset = (preset, page = 1, size = pageSize) => {
    setCurrentPage(page);

    setFilters((prev) => ({
      ...prev,
      period: preset,
      startDate: null,
      endDate: null,
      offset: (page - 1) * size,
      limit: size,
      sortColumn,
      sortDirection,
    }));
  };

  //export file as csv
  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map((c) => `"${c.label}"`).join(",");
    const rows = data.map((r) =>
      columns.map((c) => `"${r[c.key] ?? ""}"`).join(",")
    );

    const blob = new Blob([headers, "\n", rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "hourly-sales-report.csv";
    link.click();
  };

  const applyCustomDate = (page = 1, size = pageSize) => {
    setCurrentPage(page);

    setFilters((prev) => ({
      ...prev,
      period: null,
      startDate,
      endDate,
      offset: (page - 1) * size,
      limit: size,
      sortColumn,
      sortDirection,
    }));
  };

  useEffect(() => {
    applyPreset("today");
  }, []);

  const summaryCards = useMemo(
    () => [
      {
        title: "Total Orders",
        value: (summary?.totalOrders ?? 0).toLocaleString(),
        icon: Clock,
      },
      {
        title: "Total Sales",
        value: `₹${(summary?.totalSales ?? 0).toLocaleString()}`,
        icon: Clock,
      },
      {
        title: "Peak Hour",
        value: summary?.peakHour || "—",
        icon: Clock,
      },
      {
        title: "Peak Sales",
        value: `₹${(summary?.peakSales ?? 0).toLocaleString()}`,
        icon: Clock,
      },
    ],
    [summary]
  );

  const columns = [
    { key: "hour_label", label: "Hour", sortable: true },
    { key: "orders", label: "Orders", sortable: true, align: "right" },
    {
      key: "items_sold",
      label: "Items Sold",
      sortable: true,
      align: "right",
    },
    {
      key: "gross_sales",
      label: "Gross Sales",
      sortable: true,
      align: "right",
      render: (v) => `₹${Number(v ?? 0).toLocaleString()}`,
    },
    {
      key: "net_sales",
      label: "Net Sales",
      sortable: true,
      align: "right",
      render: (v) => (
        <span className="font-semibold">
          ₹{Number(v ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "avg_order",
      label: "Avg Order",
      sortable: true,
      align: "right",
      render: (v) => `₹${Number(v ?? 0).toLocaleString()}`,
    },
  ];

  const filters = (
    <div className="flex items-end gap-3 flex-wrap">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={(preset) => {
          setSelectedPreset(preset);
          setIsCustomDate(false);
          setCurrentPage(1);
          applyPreset(preset);
        }}
        onStartDateChange={(d) => {
          setStartDate(formatDate(d));
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
        onEndDateChange={(d) => {
          setEndDate(formatDate(d));
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
      />

      <button
        disabled={!isCustomDate}
        onClick={() => applyCustomDate()}
        className={`h-9 px-5 rounded-lg text-sm font-medium ${
          isCustomDate
            ? "bg-teal-600 text-white hover:bg-teal-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        Apply Filters
      </button>

      {branchOptions.length > 0 && (
        <div className="relative">
          <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedBranchId(val);
              setCurrentPage(1);
              setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
            }}
            className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
          >
            <option value="">All Branches</option>
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      )}
    </div>
  );
  
    //SORTING
const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;
  const sorted = [...data];
  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === "hour_label") {
      aVal = a.hour_no;
      bVal = b.hour_no;
    }

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }

    return sortDirection === "ASC"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in hour label (convert to string first)
    if (String(row.hour_label || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['orders', 'items_sold', 'gross_sales', 'net_sales', 'avg_order'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Hourly Sales Report"
      description="Hour-wise sales breakdown to identify peak hours"
      icon={Clock}
      exportPermission={P.REPORTS.SALES.HOURLY_EXPORT}
      color="teal"
      category="Sales"
      categoryId="sales"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      pagination={{
        currentPage,
        totalPages: Math.ceil(totalItems / pageSize),
        totalItems,
        pageSize,
        onPageChange: (page) =>
          isCustomDate
            ? applyCustomDate(page)
            : applyPreset(selectedPreset, page),
        onPageSizeChange: (size) => {
          setPageSize(size);
          isCustomDate
            ? applyCustomDate(1, size)
            : applyPreset(selectedPreset, 1, size);
        },
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
onSort={(column, direction) => {
  let nextDirection;

  if (sortColumn === column) {
    nextDirection = sortDirection === "asc" ? "desc" : "asc";
  } else {
    nextDirection = column === "hour_label" ? "asc" : "desc";
  }

  setSortColumn(column);
  setSortDirection(nextDirection);
}}






      onRefresh={() =>
        isCustomDate ? applyCustomDate() : applyPreset(selectedPreset)
      }
      onExport={(format) => {
        if (format === "csv") exportToCsv();
      }}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by hour, sales, orders..."
    />
  );
}
